/**
 * Photo URLs and photo bytes, in one place.
 *
 * Ported from Csa/report/photos.js. Electrical keys its own S3 objects
 * `elec/{audit_id}/{local_id}.jpg`, with the small variant alongside as
 * `{local_id}_t.jpg}`, same convention as CSA's `csa/...` keys — so the
 * thumbnail can be derived rather than carried twice in the report model.
 *
 * Using the thumbnail is not a nicety — CSA's export spike measured 178 MB
 * against 28 MB for this same choice. A report that embeds full-size 1600px
 * photographs is one nobody can email.
 */

const KEYED = /\/elec\/\d+\/[^/]+\.jpg$/i;

/** The small variant, where one can be derived. Anything else is returned as-is. */
export function thumbFor(url) {
  if (typeof url !== "string") return url;
  return KEYED.test(url) ? url.replace(/\.jpg$/i, "_t.jpg") : url;
}

/** Exported for the tests, so the prefix cannot be changed unnoticed. */
export const THUMBNAIL_KEY_PATTERN = KEYED;

/* The audit and photo this URL belongs to, pulled back out of the S3 key. */
const KEY_PARTS = /\/elec\/([0-9]+)\/([^/]+?)(?:_t)?[.]jpg$/i;

export function idsFrom(url) {
  const m = typeof url === "string" && url.match(KEY_PARTS);
  return m ? { auditId: m[1], localId: m[2] } : null;
}

/*
 * How many photographs are fetched at once.
 *
 * Eight is enough to saturate a browser's six-per-host connection limit
 * without building an unbounded queue of pending buffers in our own memory —
 * the same reasoning CSA's exporter uses.
 */
const CONCURRENCY = 8;

/* A single photograph's patience — a request that never answers must not
   block the whole export forever. */
const TIMEOUT_MS = 15000;

/** Every distinct photo URL the report will print. */
function urlsIn(model) {
  const urls = new Set();
  (model.sections || []).forEach((s) => {
    const d = s.data || {};
    (d.rows || []).forEach((r) => (r.photos || []).forEach((u) => urls.add(u)));
    (d.areas || []).forEach((a) =>
      (a.categories || []).forEach((c) =>
        (c.findings || []).forEach((f) => (f.photos || []).forEach((u) => urls.add(u)))));
  });
  return [...urls];
}

async function fetchOne(url, report, viaApi) {
  /*
   * Through the API first — the objects are public and CORS is open, but a
   * browser extension or a corporate proxy still turns a direct S3 fetch into
   * "Failed to fetch" often enough to matter. The API is the origin the app
   * is already talking to, so it works wherever the app does.
   */
  const ids = viaApi && idsFrom(url);
  if (ids) {
    try {
      return await viaApi(ids.auditId, ids.localId);
    } catch (e) {
      report(url, "api: " + ((e && e.message) || "failed"));
    }
  }

  const ctrl = typeof AbortController === "function" ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), TIMEOUT_MS) : null;
  const asked = thumbFor(url);
  try {
    const res = await fetch(asked, ctrl ? { signal: ctrl.signal } : undefined);
    if (!res.ok) {
      report(asked, "HTTP " + res.status);
      return null;
    }
    return new Uint8Array(await res.arrayBuffer());
  } catch (e) {
    report(asked, (e && e.name === "AbortError")
      ? "timed out after " + TIMEOUT_MS + "ms"
      : (e && e.message) || "fetch failed");
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Fetch every photograph the report will print, as bytes.
 *
 * `docx` and `exceljs` embed images from data, not from a URL, so they have
 * to be in hand before the document is built. react-pdf fetches its own.
 *
 * Never throws, and never hangs — a photograph that will not load must not
 * cost an auditor their whole export.
 */
export async function fetchPhotos(model, viaApi) {
  const urls = urlsIn(model);
  const out = new Map();
  let missing = 0;

  const failures = [];
  const report = (asked, why) => {
    failures.push({ url: asked, why });
    if (failures.length === 1) {
      console.warn("Electrical photo fetch failed: " + why, asked);
    }
  };

  let next = 0;
  const worker = async () => {
    while (next < urls.length) {
      const u = urls[next++];
      const bytes = await fetchOne(u, report, viaApi);
      if (bytes) out.set(u, bytes);
      else missing++;
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, urls.length) }, worker)
  );

  out.missing = missing;
  out.requested = urls.length;
  out.failures = failures;
  return out;
}

export const PHOTO_FETCH = { CONCURRENCY, TIMEOUT_MS };

/** What happened to the photographs, in a sentence an auditor can act on. */
export function photoReport(images) {
  if (!images || !images.requested) return null;
  if (!images.missing) return null;
  const why = (images.failures && images.failures[0] && images.failures[0].why) || "unknown";
  return images.missing + " of " + images.requested +
    " photo" + (images.requested === 1 ? "" : "s") +
    " could not be loaded (" + why + ")";
}

/** Bytes as a data URI, which is a source every renderer can load. */
function dataUriFor(bytes) {
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return "data:image/jpeg;base64," + btoa(bin);
}

/**
 * A copy of the model with every photograph inlined.
 *
 * react-pdf fetches image sources itself and cannot send an Authorization
 * header, so it cannot be pointed at the authenticated API route the other
 * two exporters use. A data URI sidesteps that: the bytes are already in
 * hand by the time the renderer runs. The model is copied rather than
 * edited, so the caller's on-screen model never carries megabytes of base64.
 */
export async function inlinePhotos(model, viaApi) {
  const images = await fetchPhotos(model, viaApi);
  if (!images.size) return { model, images };

  const uris = new Map();
  images.forEach((bytes, url) => uris.set(url, dataUriFor(bytes)));

  const swap = (list) => (list || []).map((u) => uris.get(u) || u);
  const copy = JSON.parse(JSON.stringify(model));
  (copy.sections || []).forEach((s) => {
    const d = s.data || {};
    (d.rows || []).forEach((r) => { r.photos = swap(r.photos); });
    (d.areas || []).forEach((a) =>
      (a.categories || []).forEach((c) =>
        (c.findings || []).forEach((f) => { f.photos = swap(f.photos); })));
  });

  return { model: copy, images };
}

/*
 * A bundled asset's bytes — the cover artwork, the wordmark.
 *
 * Timed out the same way fetchOne guards a photo request above - without
 * this, a fetch that never settles (the resolved URL unreachable from
 * wherever the page is actually running, which depends on
 * window.location.href and so can differ between a local dev server and a
 * deployed one) hangs this forever, and everything downstream waiting on
 * it - including the export's own toBlob() - hangs with it.
 */
export async function assetBytes(url) {
  if (!url) return null;
  const ctrl = typeof AbortController === "function" ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), TIMEOUT_MS) : null;
  try {
    const res = await fetch(url, ctrl ? { signal: ctrl.signal } : undefined);
    if (!res.ok) return null;
    return new Uint8Array(await res.arrayBuffer());
  } catch (e) {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
